import { useDraggable } from '@dnd-kit/core'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { type Campaign } from '@/pages/schedule-agent'
import { type DraggedCalendarPost } from '../types'

interface PostItemProps {
  post: Campaign['posts'][number]
}

export function PostItem({ post }: PostItemProps) {
  const scheduledDate = new Date(post.scheduledAt)
  const dateIso = scheduledDate.toISOString().split('T')[0]!
  const hour = scheduledDate.getHours()
  const minute = scheduledDate.getMinutes()

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `post-${post.id}`,
    data: {
      id: post.id,
      type: 'post',
      currentDate: dateIso,
      currentHour: hour,
      currentMinute: minute,
    } satisfies DraggedCalendarPost,
  })

  // Format time for display
  const formatTime = (hour: number, minute: number) => {
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    const displayMinute = minute.toString().padStart(2, '0')
    const ampm = hour >= 12 ? 'PM' : 'AM'
    return `${displayHour}:${displayMinute} ${ampm}`
  }

  // Get platform styling based on platform type
  const getPostColor = () => {
    switch (post.platform) {
      case 'instagram':
        return '#E1306C'
      case 'linkedin':
        return '#0A66C2'
      case 'tiktok':
        return '#000000'
      case 'youtube':
        return '#FF0000'
      case 'facebook':
        return '#1877F2'
      default:
        return '#3F2AD8'
    }
  }

  const platformStyle = {
    textColor: 'white',
    shadow: post.platform === 'instagram' ? 'shadow-pink-500/20' : 'shadow-blue-500/20',
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            className={`absolute inset-2 cursor-move rounded-xl p-3 transition-all duration-300 ease-out ${isDragging ? 'rotate-2 scale-95 opacity-70' : 'rotate-0 scale-100 opacity-100'} backdrop-blur-xs border border-white/20 hover:-translate-y-0.5 hover:scale-105 hover:shadow-lg ${platformStyle.shadow} shadow-xs z-10`}
            style={{
              background: `linear-gradient(135deg, ${getPostColor()}, ${getPostColor()}dd)`,
              color: 'white',
              touchAction: 'none',
              userSelect: 'none',
            }}>
            <div className="space-y-1">
              <div className="drop-shadow-xs truncate text-xs font-medium">{post.topic}</div>
              <div className="flex items-center justify-between">
                <div className="drop-shadow-xs text-xs font-normal opacity-80">
                  {formatTime(hour, minute)}
                </div>
                <div className="drop-shadow-xs rounded-full bg-white/20 px-1.5 py-0.5 text-xs font-medium opacity-90">
                  {post.platform}
                </div>
              </div>
            </div>
            <div className="bg-linear-to-br pointer-events-none absolute inset-0 rounded-xl from-white/10 to-transparent"></div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-[420px] p-2">
          <pre className="max-h-[300px] overflow-auto whitespace-pre text-left font-mono">
            {JSON.stringify(post, null, 2)}
          </pre>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
